import { useState, useEffect, useCallback, useRef } from 'react';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@constants/pagination';
import { useOattSession } from '@hooks/useOattSession';
import { Table, Empty, Spin, Alert, Tag, Checkbox, Input, Tooltip } from 'antd';
import type { SelectProps, TablePaginationConfig } from 'antd';
import { ApprovalColumnHeader } from '@components/ui/ApprovalSignatureCell';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useQuery, useMutation } from '@apollo/client';
import { GET_OATT_VALIDATION, SAVE_OATT_VALIDATION } from '@graphql/oattvalidation.gql';
import { CREATE_OATT_BILLING_FORM } from '@graphql/oattinvoice.gql';
import PageLayout from '@components/layout/PageLayout';
import { NSPDatePicker } from '@components/ui/NSPDatepicker';
import { NSPButton } from '@components/ui/NSPButton';
import styles from './OattPages.module.css';
import { useMsal, useAccount } from '@azure/msal-react';
import useSession from '@hooks/useSession';
import { useNavigate } from 'react-router-dom';
import { useOattCustomer } from '@hooks/useOattcustomer';
import { testPaginationItemRender, withTestColumnAttrs, type TestAttrs } from '@utils/testAttr';

dayjs.extend(utc);

// ── Types ─────────────────────────────────────────────────────────────────────

interface OattValidationRecord {
  oatt_computed_id: number;
  raw_id: number;
  seller: string;
  aref: string;
  customer_code: string;
  billing_period: string;
  billing_type: string | null;
  mw_granted: number | null;
  billing_rate: number | null;
  hours: number | null;
  start_time: string | null;
  stop_time: string | null;
  firm_transmission_charge_oatt: number | null;
  nonfirm_transmission_charge_oatt: number | null;
  ancillary_schedule1_oatt: number | null;
  ancillary_schedule2_oatt: number | null;
  total_charges_oatt: number | null;
  firm_transmission_charge_computed: number | null;
  nonfirm_transmission_charge_computed: number | null;
  ancillary_schedule1_computed: number | null;
  ancillary_schedule2_computed: number | null;
  total_charges_computed: number | null;
  adjustments: number | null;
  oatt_data_source_selected: boolean | null;
  comments: string | null;
  approval: boolean | null;
  approval_signature: string | null;
  validated_status: boolean | null;
  validated_at: string | null;
  approved_at: string | null;
  is_revision: boolean | null;
  revision_no: number | null;
}

interface RowDraft {
  oatt_data_source_selected: boolean;
  comments: string;
  approval: boolean;
  approval_signature: string;
}

// ── Formatters ────────────────────────────────────────────────────────────────

const fmtMoney = (v: number | null | undefined): string => {
  if (v == null) return '—';
  return Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDateTime = (v: string | null | undefined): string => {
  if (!v) return '—';
  const ms = Number(v);
  if (!isNaN(ms) && ms > 0) return dayjs(ms).format('M/D/YYYY H:mm');
  const d = dayjs(v);
  return d.isValid() ? d.format('M/D/YYYY H:mm') : '—';
};

const fmtDateOnly = (v: string | null | undefined): string => {
  if (!v) return '—';
  const d = dayjs(v);
  return d.isValid() ? d.format('M/D/YYYY') : '—';
};

const netOatiCharge = (r: OattValidationRecord): number | null => {
  const firm = r.firm_transmission_charge_oatt ?? 0;
  const nf = r.nonfirm_transmission_charge_oatt ?? 0;
  const v = firm + nf;
  return v === 0 && r.firm_transmission_charge_oatt == null && r.nonfirm_transmission_charge_oatt == null ? null : v;
};

const netComputedCharge = (r: OattValidationRecord): number | null => {
  const firm = r.firm_transmission_charge_computed ?? 0;
  const nf = r.nonfirm_transmission_charge_computed ?? 0;
  const v = firm + nf;
  return v === 0 && r.firm_transmission_charge_computed == null && r.nonfirm_transmission_charge_computed == null ? null : v;
};

const difference = (r: OattValidationRecord): number | null => {
  if (r.total_charges_oatt == null || r.total_charges_computed == null) return null;
  return r.total_charges_oatt - r.total_charges_computed;
};

// Child columns repeat titles across the OATT DATA / OATT COMPUTED groups, so cells are named by group
const COLUMN_LABELS: Record<string, string> = {
  net_oati: 'OATT Data Net Charge',
  sc1_oati: 'OATT Data SC1 Charge',
  sc2_oati: 'OATT Data SC2 Charge',
  tot_oati: 'OATT Data Total',
  net_comp: 'OATT Computed Net Charge',
  sc1_comp: 'OATT Computed SC1 Charge',
  sc2_comp: 'OATT Computed SC2 Charge',
  tot_comp: 'OATT Computed Total',
  approval: 'Approval',
};

const differenceStatus = (diff: number): string => {
  if (diff === 0) return 'match';
  return diff > 0 ? 'over' : 'under';
};

// ── Component ─────────────────────────────────────────────────────────────────

const OattvalidationPage = () => {
  const originalDraftsRef = useRef<Record<number, RowDraft>>({});
  const navigate = useNavigate();
  const { customerId, customerCode, CustomerSelect } = useOattCustomer();

  const { selectedDate, setSelectedDate } = useOattSession();
  const { accounts } = useMsal();
  const msalAccount = useAccount(accounts[0] || {});
  const username = msalAccount?.name ?? msalAccount?.username ?? '';
  const { isOattBillPreparer, isOattBillApprover } = useSession();

  const canEditValidationFields = isOattBillPreparer;
  const canEditApproval = isOattBillApprover;
  const canSave = isOattBillPreparer || isOattBillApprover;
  // Approver-only users have read-only access except for the approval checkbox —
  // Save Validation stays disabled for them until they check an approval box.
  const isApproverOnly = isOattBillApprover && !isOattBillPreparer;

  const [drafts, setDrafts] = useState<Record<number, RowDraft>>({});
  const [approverDirty, setApproverDirty] = useState(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  /**
   * Tracks which oatt_computed_ids have been persisted to the server with
   * approval=true. Used to derive the revision-level lock.
   */
  const [savedApprovals, setSavedApprovals] = useState<Set<number>>(new Set());

  type SaveStatus = { type: 'success' | 'error'; message: string } | null;
  const [saveStatus, setSaveStatus] = useState<SaveStatus>(null);
  const [recordsState, setRecordsState] = useState<OattValidationRecord[]>([]);

  const selectedMonth = selectedDate.month() + 1;
  const selectedYear = selectedDate.year();

  const { data, loading, error, refetch } = useQuery<{ getOattValidation: OattValidationRecord[] }>(
    GET_OATT_VALIDATION,
    {
      variables: { month: selectedMonth, year: selectedYear, customerId },
      skip: customerId === null,
      fetchPolicy: 'no-cache',

    },
  );
  const records = recordsState;
  /**
   * True when every row for this revision has been saved with approval=true.
   * Locks all editing for the entire revision once the approval is persisted.
   */
  const isRevisionLocked =
    records.length > 0 && records.every((r) => savedApprovals.has(r.oatt_computed_id));

  const isRevisionData = records.length > 0 && Boolean(records[0].is_revision);

  const pageTitle = `${customerCode ? `${customerCode} - ` : ''
    }${isRevisionData ? 'Revised ' : ''
    }${selectedDate.format('MMMM YYYY')} OATT Validation`;

  const billingFormLabel = isRevisionData
    ? `Create ${selectedDate.format('MMMM')} ${selectedYear} — Revised Billing Form`
    : `Create ${selectedDate.format('MMMM')} ${selectedYear} — Billing Form`;

  /**
   * Single unified effect for all query-driven state.
   *
   * recordsState and drafts/savedApprovals are guarded independently:
   *
   *  - recordsState → guarded only against undefined/null data (transient Apollo
   *    no-cache state). Updated whenever valid data is present so the table never
   *    goes blank, even if Apollo emits loading=true alongside valid data.
   *
   *  - drafts / savedApprovals → additionally guarded against loading=true so
   *    user edits and approval state are not reset mid-flight. Both are committed
   *    in the same effect call as recordsState when loading=false, ensuring
   *    records and drafts are always in sync (no dashed-cell race window).
   */
  useEffect(() => {
    console.log('[OattValidation] effect fired — loading:', loading, '| raw rows:', data?.getOattValidation?.length ?? 'n/a');

    // Transient undefined: Apollo's no-cache policy clears data between request
    // start and response arrival. Preserve existing recordsState in this window.
    if (!data?.getOattValidation) {
      console.log('[OattValidation] effect guard: data?.getOattValidation is falsy — skipping');
      return;
    }

    // Do NOT filter here. The previous deduplication used `!r?.oatt_computed_id`
    // (a falsy check) which silently dropped every record whose oatt_computed_id
    // is 0. Take the raw array from Apollo as-is and let the rowKey handle
    // uniqueness for the table.
    const activeRecords = data.getOattValidation;

    console.log('[OattValidation] setRecordsState →', activeRecords.length, 'rows | IDs:', activeRecords.map((r) => r.oatt_computed_id));

    // Guard against Apollo's post-mutation broadcast artifact.
    //
    // When the saveValidation mutation completes, Apollo calls broadcastQueries()
    // which causes the no-cache ObservableQuery to re-emit its lastResult through
    // the observable pipeline. In this intermediate emission, records are present
    // (Array(N)) but oatt_computed_id is absent from every record because the
    // field hasn't been resolved in this broadcast context — it is NOT a real
    // server response. The actual refetch() result (with correct IDs) arrives
    // moments later.
    //
    // Skipping this emission preserves the previous recordsState so the table
    // never goes blank, and the correct data from the real refetch renders cleanly.
    if (activeRecords.length > 0 && activeRecords.every((r) => r.oatt_computed_id == null)) {
      console.log('[OattValidation] skip: all oatt_computed_ids null/undefined — transient broadcast, preserving existing state');
      return;
    }

    // Commit records to stable local state whenever valid data is available.
    // The Table always reads from recordsState, never from the transient Apollo
    // response. This runs regardless of loading so save/refetch never clears rows.
    setRecordsState(activeRecords);

    // Don't reinitialize drafts or savedApprovals while Apollo is mid-flight.
    // recordsState is already updated above; this guard only prevents
    // user-editable state from being reset during the loading phase.
    if (loading) {
      console.log('[OattValidation] effect guard: loading=true — skipping drafts reinit');
      return;
    }

    if (activeRecords.length === 0) {
      console.log('[OattValidation] activeRecords empty — clearing drafts');
      setDrafts({});
      originalDraftsRef.current = {};
      setSavedApprovals(new Set());
      setApproverDirty(false);
      return;
    }

    const initial: Record<number, RowDraft> = {};
    const initialSaved = new Set<number>();

    for (const r of activeRecords) {
      // Strict null check: oatt_computed_id=0 is a valid (if unusual) value and
      // must NOT be excluded. The old falsy `!r.oatt_computed_id` guard was the
      // root cause of drafts being empty when all IDs are 0.
      if (r?.oatt_computed_id == null) continue;

      initial[r.oatt_computed_id] = {
        oatt_data_source_selected: r.oatt_data_source_selected ?? true,
        comments: r.comments ?? '',
        approval: r.approval ?? false,
        approval_signature: r.approval_signature ?? '',
      };
      if (r.approval === true) initialSaved.add(r.oatt_computed_id);
    }

    console.log('[OattValidation] setDrafts + setSavedApprovals —', Object.keys(initial).length, 'entries | IDs:', Object.keys(initial));
    setDrafts(initial);
    originalDraftsRef.current = initial;
    setApproverDirty(false);
    // Merge — never shrink savedApprovals from a refetch.
    // The server may return approval=false due to read-after-write timing on the
    // first query after a save. Since mutation success already confirmed the save,
    // we keep previously confirmed IDs and only ever add new ones from the server.
    setSavedApprovals((prev) => {
      const merged = new Set(prev);
      for (const id of initialSaved) merged.add(id);
      return merged;
    });
  }, [data, loading]);

  const updateDraft = useCallback((id: number, patch: Partial<RowDraft>) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }, []);

  // ── Bulk approval ────────────────────────────────────────────────────────────

  // Rows eligible for bulk approve: not yet server-persisted
  const bulkEligibleIds = records
    .filter((r) => !savedApprovals.has(r.oatt_computed_id))
    .map((r) => r.oatt_computed_id);

  const bulkCheckedCount = bulkEligibleIds.filter(
    (id) => drafts[id]?.approval
  ).length;

  const bulkHeaderState = {
    checked: bulkEligibleIds.length > 0 && bulkCheckedCount === bulkEligibleIds.length,
    indeterminate: bulkCheckedCount > 0 && bulkCheckedCount < bulkEligibleIds.length,
    disabled: isRevisionLocked || bulkEligibleIds.length === 0,
  };

  const handleBulkApproveChange = useCallback((checked: boolean) => {
    setDrafts((prev) => {
      const next = { ...prev };
      for (const id of bulkEligibleIds) {
        next[id] = {
          ...next[id],
          approval: checked,
          approval_signature: checked ? username : '',
        };
      }
      return next;
    });
    setApproverDirty(true);
  }, [bulkEligibleIds, username]);
  const [saveValidation, { loading: saving }] = useMutation(SAVE_OATT_VALIDATION);
  const [createBillingForm, { loading: creatingForm }] = useMutation(CREATE_OATT_BILLING_FORM);

  const handleSave = async () => {
    setSaveStatus(null);

    if (!records.length) {
      setSaveStatus({ type: 'error', message: 'No records to save.' });
      return;
    }

    // Frontend guard: block saves once the entire revision is locked server-side
    if (isRevisionLocked) {
      setSaveStatus({
        type: 'error',
        message: 'This revision is approved and locked. No further changes can be saved.',
      });
      return;
    }

    // Backend guard: exclude rows that were already persisted as approved so
    // the server never receives a re-save of a locked record.
    const payload = records
      .filter((r) => !savedApprovals.has(r.oatt_computed_id))
      .map((r) => {
        const d = drafts[r.oatt_computed_id] ?? {
          oatt_data_source_selected: true, comments: '', approval: false, approval_signature: '',
        };
        return {
          oatt_computed_id: r.oatt_computed_id,
          oatt_data_source_selected: d.oatt_data_source_selected,
          comments: d.comments || null,
          approval: d.approval,
          approval_signature: d.approval_signature || null,
        };
      });

    if (!payload.length) {
      setSaveStatus({ type: 'error', message: 'No changes to save.' });
      return;
    }

    try {
      const res = await saveValidation({ variables: { records: payload } });
      if (res.data?.saveOattValidation?.success) {
        originalDraftsRef.current = { ...drafts };
        setApproverDirty(false);

        // Optimistically lock rows whose approval was just saved.
        // The server confirmed success, so we trust the draft state immediately
        // rather than waiting for the refetch (which may have read-after-write
        // lag and return approval=false, causing the checkbox to briefly re-enable).
        setSavedApprovals((prev) => {
          const merged = new Set(prev);
          for (const r of records) {
            if (drafts[r.oatt_computed_id]?.approval) merged.add(r.oatt_computed_id);
          }
          return merged;
        });

        // Re-fetch from the server so savedApprovals and isRevisionLocked are
        // always derived from the server's ground truth, not guessed client state.
        // The useEffect will re-initialize savedApprovals from the fresh response.
        console.log('[OattValidation] handleSave: mutation succeeded — calling refetch()');
        const refetchResult = await refetch();
        console.log('[OattValidation] handleSave: refetch() resolved —', refetchResult.data?.getOattValidation?.length ?? 'no data', 'rows returned');
        setSaveStatus({ type: 'success', message: 'Validation saved successfully' });
      } else {
        setSaveStatus({ type: 'error', message: 'Save failed.' });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setSaveStatus({ type: 'error', message: `Save failed: ${msg}` });
    }
  };

  const handleCreateBillingForm = async () => {
    setSaveStatus(null);
    const notApproved = records.some(
      (r) => !(drafts[r.oatt_computed_id]?.approval ?? false)
    );
    if (notApproved) {
      setSaveStatus({
        type: 'error',
        message: 'Billing form cannot be created. Some records are not approved yet.',
      });
      return;
    }
    try {
      const res = await createBillingForm({
        variables: { month: selectedMonth, year: selectedYear, customerId },
      });
      if (res.data?.createOattBillingForm?.success) {
        setSaveStatus({
          type: 'success',
          message: res.data.createOattBillingForm.message || 'Billing form created.',
        });
        setTimeout(() => navigate('/oati/invoice'), 800);
      } else {
        setSaveStatus({
          type: 'error',
          message: res.data?.createOattBillingForm?.message || 'Failed to create billing form.',
        });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setSaveStatus({ type: 'error', message: `Billing form failed: ${msg}` });
    }
  };

  const disabledDate = (current: Dayjs) => current && current >= dayjs().startOf('month');

  // ── Columns ───────────────────────────────────────────────────────────────

  const columns: ColumnsType<OattValidationRecord> = [
    { title: 'Seller', dataIndex: 'seller', key: 'seller', width: 80, fixed: 'left', render: (v) => v || '—' },
    { title: 'AREF', dataIndex: 'aref', key: 'aref', width: 120, fixed: 'left', render: (v) => v || '—' },
    { title: 'Customer', dataIndex: 'customer_code', key: 'customer_code', width: 90, fixed: 'left', render: (v) => v || '—' },
    { title: 'Billing Period', dataIndex: 'billing_period', key: 'billing_period', width: 110, fixed: 'left', render: fmtDateOnly },
    { title: 'Billing Type', dataIndex: 'billing_type', key: 'billing_type', width: 110, fixed: 'left', render: (v: string | null) => v || '—' },
    { title: 'MW Granted', dataIndex: 'mw_granted', key: 'mw_granted', width: 100, align: 'right' as const, render: (v: number | null) => v != null ? Number(v).toLocaleString() : '—' },
    { title: 'Billing Rate', dataIndex: 'billing_rate', key: 'billing_rate', width: 110, align: 'right' as const, render: (v: number | null) => v != null ? `$${Number(v).toFixed(4)}` : '—' },
    { title: 'Hours', dataIndex: 'hours', key: 'hours', width: 80, align: 'right' as const, render: (v: number | null) => v != null ? Number(v).toLocaleString() : '—' },
    { title: 'Start Time', dataIndex: 'start_time', key: 'start_time', width: 140, render: fmtDateTime },
    { title: 'Stop Time', dataIndex: 'stop_time', key: 'stop_time', width: 140, render: fmtDateTime },

    {
      title: 'OATT DATA',
      className: 'group-header-oati',
      children: [
        { title: 'Net Charge', key: 'net_oati', width: 130, align: 'right' as const, render: (_: unknown, r: OattValidationRecord) => fmtMoney(netOatiCharge(r)) },
        { title: 'SC1 Charge', dataIndex: 'ancillary_schedule1_oatt', key: 'sc1_oati', width: 120, align: 'right' as const, render: fmtMoney },
        { title: 'SC2 Charge', dataIndex: 'ancillary_schedule2_oatt', key: 'sc2_oati', width: 120, align: 'right' as const, render: fmtMoney },
        { title: 'Total', dataIndex: 'total_charges_oatt', key: 'tot_oati', width: 130, align: 'right' as const, render: (v: number | null) => <strong>{fmtMoney(v)}</strong> },
      ],
    },
    {
      title: 'OATT COMPUTED',
      className: 'group-header-computed',
      children: [
        { title: 'Net Charge', key: 'net_comp', width: 130, align: 'right' as const, render: (_: unknown, r: OattValidationRecord) => fmtMoney(netComputedCharge(r)) },
        { title: 'SC1 Charge', dataIndex: 'ancillary_schedule1_computed', key: 'sc1_comp', width: 120, align: 'right' as const, render: fmtMoney },
        { title: 'SC2 Charge', dataIndex: 'ancillary_schedule2_computed', key: 'sc2_comp', width: 120, align: 'right' as const, render: fmtMoney },
        { title: 'Total', dataIndex: 'total_charges_computed', key: 'tot_comp', width: 130, align: 'right' as const, render: (v: number | null) => <strong>{fmtMoney(v)}</strong> },
      ],
    },
    {
      title: 'Difference',
      key: 'difference',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, r: OattValidationRecord) => {
        const diff = difference(r);
        if (diff == null) return '—';
        return <Tag color={diff === 0 ? 'green' : diff > 0 ? 'orange' : 'red'} style={{ fontWeight: 600 }} data-testid="difference-tag" data-difference={differenceStatus(diff)}>{fmtMoney(diff)}</Tag>;
      },
    },
    {
      title: 'Use OATT Data for Invoice',
      key: 'oatt_data_source_selected',
      width: 130,
      align: 'center' as const,
      render: (_: unknown, r: OattValidationRecord) => (
        <Checkbox
          checked={drafts[r.oatt_computed_id]?.oatt_data_source_selected ?? true}
          disabled={
            isRevisionLocked ||
            !canEditValidationFields ||
            drafts[r.oatt_computed_id]?.approval
          }
          onChange={(e) => updateDraft(r.oatt_computed_id, { oatt_data_source_selected: e.target.checked })}
          data-testid="use-oatt-data-checkbox"
        />
      ),
    },
    {
      title: 'Comments',
      key: 'comments',
      width: 180,
      render: (_: unknown, r: OattValidationRecord) => (
        <Input
          value={drafts[r.oatt_computed_id]?.comments ?? ''}
          placeholder="Enter comments"
          data-testid="validation-comment-input"
          maxLength={200}
          size="small"
          disabled={
            isRevisionLocked ||
            !canEditValidationFields ||
            drafts[r.oatt_computed_id]?.approval
          }
          onChange={(e) => updateDraft(r.oatt_computed_id, { comments: e.target.value.replace(/[^\x20-\x7E -￿\uD800-\uDFFF]/g, '') })}
          style={{ width: 160 }}
        />
      ),
    },
    {
      title: () => (
        <ApprovalColumnHeader
          canApprove={canEditApproval}
          checked={bulkHeaderState.checked}
          indeterminate={bulkHeaderState.indeterminate}
          disabled={bulkHeaderState.disabled}
          onChange={handleBulkApproveChange}
        />
      ),
      key: 'approval',
      width: 90,
      align: 'center' as const,
      render: (_: unknown, r: OattValidationRecord) => {
        const isSavedApproved = savedApprovals.has(r.oatt_computed_id);
        return (
          <Checkbox
            checked={drafts[r.oatt_computed_id]?.approval ?? false}
            disabled={!canEditApproval || isSavedApproved}
            data-testid="approve-checkbox"
            onChange={(e) => {
              const checked = e.target.checked;
              updateDraft(r.oatt_computed_id, {
                approval: checked,
                approval_signature: checked ? username : '',
              });
              setApproverDirty(true);
            }}
          />
        );
      },
    },
    {
      title: 'Approval Signature',
      key: 'approval_signature',
      width: 180,
      render: (_: unknown, r: OattValidationRecord) => {
        const draft = drafts[r.oatt_computed_id];
        const isSavedApproved = savedApprovals.has(r.oatt_computed_id);

        // Read-only once approval has been persisted for this revision row
        if (isSavedApproved) {
          return (
            <span style={{ fontStyle: 'italic', color: '#555' }} data-testid="approver-signature">
              {draft?.approval_signature || '—'}
            </span>
          );
        }

        const isApproved = draft?.approval ?? false;
        const displayValue = draft?.approval_signature ?? (isApproved ? username : '');
        return (
          <Tooltip title={isApproved ? '' : 'Enable Approval first'}>
            <Input
              value={displayValue}
              placeholder="Signature"
              data-testid="approver-signature-input"
              size="small"
              disabled={!canEditApproval || !isApproved}
              onFocus={() => {
                // Auto-populate on focus if signature is still empty
                if (canEditApproval && isApproved && !draft?.approval_signature && username) {
                  updateDraft(r.oatt_computed_id, { approval_signature: username });
                }
              }}
              onChange={(e) => updateDraft(r.oatt_computed_id, { approval_signature: e.target.value })}
              style={{ width: 160 }}
            />
          </Tooltip>
        );
      },
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <PageLayout
        title={pageTitle}
        headerAccessoryView={
          <div className={styles.periodSelector} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className={styles.customerSelectWrapper}>
              <CustomerSelect />
            </div>
            <NSPDatePicker
              picker="month"
              format="MMM YYYY"
              allowClear={false}
              value={selectedDate}
              onChange={(date) => {
                if (date) {
                  setSelectedDate(date);
                  setSaveStatus(null);
                }
              }}
              disabledDate={disabledDate}
              testId="month-picker"
            />
          </div>
        }
      >
        <div className={styles.content}>
          {isRevisionLocked && (
            <Alert
              type="info"
              data-testid="validation-locked-alert"
              message={`This ${isRevisionData ? `Revision ${records[0]?.revision_no} ` : ''}validation is approved and locked. No further edits are permitted.`}
              showIcon
              className={styles.alertBanner}
            />
          )}

          {saveStatus && (
            <Alert
              type={saveStatus.type}
              message={saveStatus.message}
              showIcon
              closable
              onClose={() => setSaveStatus(null)}
              data-testid="validation-status-alert"
              data-type={saveStatus.type}
              className={styles.alertBanner}
            />
          )}

          {error ? (
            <Empty description={`Error loading data: ${error.message}`} data-testid="oatt-validation-error" />
          ) : (
            <Table<OattValidationRecord>
              className={styles.oattTable}
              dataSource={records}
              columns={withTestColumnAttrs(
                columns.map((col) =>
                  'children' in col
                    ? { ...col, children: withTestColumnAttrs(col.children, 'oatt-validation-value', COLUMN_LABELS) }
                    : col,
                ),
                'oatt-validation-value',
                COLUMN_LABELS,
              )}
              loading={loading ? { spinning: true, indicator: <Spin size="large" /> } : false}
              rowKey={(r) => String(r.oatt_computed_id || r.raw_id)}
              data-testid="oatt-validation-table"
              data-loading={String(loading)}
              data-locked={String(isRevisionLocked)}
              onRow={(r) => {
                const diff = difference(r);
                return {
                  'data-testid': 'oatt-validation-row',
                  'data-id': String(r.oatt_computed_id),
                  'data-aref': r.aref ?? '',
                  'data-approved': String(savedApprovals.has(r.oatt_computed_id)),
                  'data-difference': diff == null ? '' : differenceStatus(diff),
                } as TestAttrs;
              }}
              rowClassName={(r) => {
                const diff = difference(r);
                if (diff == null) return '';
                return diff !== 0 ? styles.rowMismatch : styles.rowMatch;
              }}
              pagination={{
                pageSize,
                showTotal: (total) => <span data-testid="pagination-total">{`Total ${total} records`}</span>,
                showSizeChanger: { 'data-testid': 'page-size-select' } as SelectProps,
                pageSizeOptions: PAGE_SIZE_OPTIONS,
                onShowSizeChange: (_, size) => setPageSize(size),
                itemRender: testPaginationItemRender,
                'data-testid': 'table-pagination',
              } as TablePaginationConfig}
              scroll={{ x: 'max-content', y: 'calc(100vh - 340px)' }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    data-testid="oatt-validation-empty"
                    description={
                      customerId === null
                        ? 'Select a customer above'
                        : `No data for ${selectedDate.format('MMMM YYYY')}. Run QC Compute OATT first.`
                    }
                  />
                ),
              }}
            />
          )}
        </div>

        <div className={styles.computeButtonContainer}>
          {canSave && (
            <NSPButton
              onClick={handleSave}
              disabled={
                records.length === 0 ||
                customerId === null ||
                isRevisionLocked ||
                (isApproverOnly && !approverDirty)
              }
              loading={saving}
              data-testid="save-validation-button"
            >
              SAVE VALIDATION
            </NSPButton>
          )}

          {records.length > 0 && canSave && (
            <NSPButton
              onClick={handleCreateBillingForm}
              loading={creatingForm}
              disabled={isOattBillApprover}
              data-testid="create-billing-form-button"
            >
              {billingFormLabel}
            </NSPButton>
          )}
        </div>
      </PageLayout>
    </>
  );
};

export default OattvalidationPage;